import { z } from "zod";
import { ANDROID_APP } from "./android";
import type { InAppPlanCode, PurchasablePlanCode } from "./billing";

/**
 * Google Play sells what the App Store sells (owner, 2026-11-03): Premium only (`IN_APP_PLAN_CODE`), one Play Console
 * product with a monthly and an annual base plan, at the web's Premium price. Pro, its capacity packs and Business stay
 * on the web (Paddle / invoice); a plan or pack bought there still counts here because they belong to the account.
 * `bridewell_pro` is no longer sold but stays recognised: a Pro subscription bought on Play before keeps its tier.
 */
export const GOOGLE_PLAY = {
  packageName: ANDROID_APP.applicationId,
  products: { premium: "bridewell_premium", pro: "bridewell_pro" },
  basePlans: { month: "monthly", year: "annual" },
} as const;

/** The Play Console product + base plan that sells a plan code — only what the app stores sell. */
export function playSubscriptionFor(code: InAppPlanCode): { productId: string; basePlanId: string } {
  const tier = code.startsWith("pro") ? "pro" : "premium";
  return { productId: GOOGLE_PLAY.products[tier], basePlanId: code.endsWith("annual") ? GOOGLE_PLAY.basePlans.year : GOOGLE_PLAY.basePlans.month };
}

/** Back from a Play product + base plan to the plan code (also for products no longer sold); null for anything else. */
export function planCodeForPlay(productId: string | null | undefined, basePlanId: string | null | undefined): PurchasablePlanCode | null {
  const tier = productId === GOOGLE_PLAY.products.premium ? "premium" : productId === GOOGLE_PLAY.products.pro ? "pro" : null;
  if (!tier) return null;
  if (basePlanId === GOOGLE_PLAY.basePlans.month) return `${tier}_monthly`;
  if (basePlanId === GOOGLE_PLAY.basePlans.year) return `${tier}_annual`;
  return null;
}

/**
 * `POST /me/billing/google/verify` — the app hands over what Google Play Billing returned after a purchase.
 * The server never trusts it: it asks Google about the token itself and only then grants anything.
 */
export const verifyPlayPurchaseSchema = z.object({
  productId: z.string().min(1).max(200),
  purchaseToken: z.string().min(10).max(4096),
});
export type VerifyPlayPurchase = z.infer<typeof verifyPlayPurchaseSchema>;

/** `GET /me/billing/google` — whether the account can buy through Google Play right now. */
export interface PlayBillingStatusDTO {
  /** False until the Google Play credentials are configured on the server; the app then offers no purchase. */
  enabled: boolean;
  packageName: string;
}
